import { useState, useEffect, useRef, useMemo } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Upload, Download, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { MARKET_TABS } from '@/lib/buyersV2';

const SKIP = '__skip__';

function SheetMapper({ sheet, fields, onChange }) {
  const mapped = Object.values(sheet.mapping).filter(Boolean).length;
  const used = new Set(Object.values(sheet.mapping).filter(Boolean));
  return (
    <div className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`import-sheet-${sheet.name}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Checkbox checked={sheet.include} onCheckedChange={v => onChange({ ...sheet, include: !!v })} data-testid={`import-include-${sheet.name}`} />
        <p className="font-semibold text-sm text-gray-900 flex-1">{sheet.name} <span className="text-gray-400 font-normal">· {sheet.row_count} rows · {mapped}/{sheet.headers.length} columns mapped</span></p>
        <Select value={sheet.market_tab || SKIP} onValueChange={v => onChange({ ...sheet, market_tab: v === SKIP ? '' : v })}>
          <SelectTrigger className="w-[170px] h-8 text-xs bg-white" data-testid={`import-market-${sheet.name}`}><SelectValue placeholder="Market tab" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value={SKIP}>From file</SelectItem>{MARKET_TABS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {sheet.unmatched.length > 0 && (
        <div className="space-y-1.5 bg-amber-50/60 border border-amber-200 rounded-md p-2">
          <p className="text-[11px] text-amber-800 font-medium">Columns we couldn't match — pick a field or leave skipped:</p>
          {sheet.unmatched.map(h => (
            <div key={h} className="flex items-center gap-2 text-xs">
              <span className="w-40 truncate text-gray-700" title={h}>{h}</span>
              <Select value={sheet.mapping[h] || SKIP} onValueChange={v => onChange({ ...sheet, mapping: { ...sheet.mapping, [h]: v === SKIP ? '' : v } })}>
                <SelectTrigger className="h-7 text-xs bg-white flex-1" data-testid={`map-${sheet.name}-${h}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-64"><SelectItem value={SKIP}>Skip column</SelectItem>{fields.map(f => <SelectItem key={f.field} value={f.field} disabled={used.has(f.field) && sheet.mapping[h] !== f.field}>{f.label}{used.has(f.field) && sheet.mapping[h] !== f.field ? ' (already mapped)' : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Summary({ job }) {
  return (
    <div data-testid="import-summary" className="space-y-2">
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center">
        {[['Created', job.created], ['Updated', job.updated], ['Skipped', job.skipped], ['Contacts', job.contacts_added], ['Purchases', job.purchases_added]].map(([l, v]) => (
          <div key={l} className="rounded-lg bg-gray-50 border border-gray-100 py-2"><p className="text-lg font-bold text-gray-900" data-testid={`import-${l.toLowerCase()}`}>{v || 0}</p><p className="text-[10px] uppercase text-gray-400">{l}</p></div>
        ))}
      </div>
      {job.errors?.length > 0 && (
        <div className="max-h-40 overflow-y-auto text-xs bg-red-50 border border-red-200 rounded-md p-2 space-y-0.5" data-testid="import-errors">
          {job.errors.map((e, i) => <p key={`${e.sheet || ''}-${e.row ?? 'x'}-${i}`} className="text-red-700">{e.sheet ? `${e.sheet} ` : ''}row {e.row ?? '?'}: {e.error}</p>)}
        </div>
      )}
    </div>
  );
}

export function ImportWizard({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState('upload');
  const [preview, setPreview] = useState(null);
  const [saveMapping, setSaveMapping] = useState(true);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const poll = useRef(null);

  useEffect(() => { if (open) { setStep('upload'); setPreview(null); setJob(null); } return () => clearInterval(poll.current); }, [open]);

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('preview', '1');
      const res = await API.post('/buyers/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview({ ...res.data, sheets: res.data.sheets.map(s => ({ ...s, include: true })) }); setStep('map');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const rowsToImport = useMemo(() => (preview?.sheets || []).filter(s => s.include).reduce((a, s) => a + s.row_count, 0), [preview]);
  const run = async () => {
    setBusy(true);
    try {
      await API.post(`/buyers/import/${preview.job_id}/run`, { sheets: preview.sheets.map(({ sample, unmatched, ...s }) => s), save_mapping: saveMapping });
      setStep('running');
      poll.current = setInterval(async () => {
        const r = await API.get(`/buyers/import/${preview.job_id}`);
        if (r.data.status !== 'processing') { clearInterval(poll.current); setJob(r.data); setStep('done'); onDone(); }
      }, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };

  const template = async () => {
    const res = await API.get('/buyers/import-template', { responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([res.data])); a.download = 'buyer_import_template.csv'; a.click();
  };

  const updateSheet = (s) => setPreview(p => ({ ...p, sheets: p.sheets.map(x => x.name === s.name ? s : x) }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-wizard">
        <DialogHeader><DialogTitle>Import Buyers (CSV / XLSX)</DialogTitle></DialogHeader>
        {step === 'upload' && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">One row per person per purchased property. Rows are grouped by Entity Name / Sunbiz Doc # into one buyer; contacts and purchases are de-duplicated. Existing buyers are updated, nothing is deleted.</p>
            <label className="block border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#0073ea]/50 transition-colors">
              {busy ? <Loader2 className="h-7 w-7 text-[#0073ea] mx-auto animate-spin" /> : <Upload className="h-7 w-7 text-gray-400 mx-auto mb-2" />}
              <p className="text-sm text-gray-600">Click to choose a .csv or .xlsx file (multi-tab supported)</p>
              <input data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={e => e.target.files[0] && upload(e.target.files[0])} />
            </label>
            <button data-testid="download-template-btn" onClick={template} className="text-xs text-[#0073ea] hover:underline flex items-center gap-1"><Download className="h-3 w-3" /> Download template with exact headers</button>
          </div>
        )}
        {step === 'map' && preview && (
          <div className="space-y-3">
            {preview.sheets.map(s => <SheetMapper key={s.name} sheet={s} fields={preview.fields} onChange={updateSheet} />)}
            <label className="flex items-center gap-2 text-xs text-gray-600"><Checkbox checked={saveMapping} onCheckedChange={v => setSaveMapping(!!v)} data-testid="import-save-mapping" /> Remember my column choices for next time</label>
            <Button data-testid="import-run-btn" onClick={run} disabled={busy || !preview.sheets.some(s => s.include)} className="w-full bg-[#0073ea] hover:bg-[#0060c2] text-white">Import {rowsToImport} rows</Button>
          </div>
        )}
        {step === 'running' && <div className="py-10 text-center" data-testid="import-running"><Loader2 className="h-8 w-8 text-[#0073ea] mx-auto animate-spin mb-2" /><p className="text-sm text-gray-600">Importing and recalculating stats…</p></div>}
        {step === 'done' && job && (
          <div className="space-y-3">
            <p className="flex items-center gap-2 text-sm font-semibold">{job.status === 'completed' ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-red-600" />} Import {job.status}</p>
            <Summary job={job} />
            <Button onClick={() => onOpenChange(false)} className="w-full" variant="outline" data-testid="import-close-btn">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export { Summary as ImportSummary };
